import React from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Lock,
  Cloud,
  Users,
  Download,
  RefreshCw,
  Undo2,
  Check,
  X,
  ArrowRight,
} from "lucide-react";
import { plans } from "@/config/site";

/* Section rule + eyebrow signature ------------------------------------- */
const SectionRule = ({ eyebrow, dark = false }) => (
  <>
    <div className="rule" style={dark ? { background: "#4FC3F7" } : {}} />
    <div className={`eyebrow ${dark ? "!text-white" : ""}`}>{eyebrow}</div>
  </>
);

/* Home page ------------------------------------------------------------ */
export default function Home() {
  return (
    <>
      {/* HERO — navy gradient */}
      <section
        className="relative overflow-hidden text-white"
        style={{ background: "linear-gradient(105deg,#0F2B46 0%,#196CA3 100%)" }}
      >
        <div className="mx-auto max-w-site px-5 lg:px-8 py-20 lg:py-28">
          <div className="max-w-3xl">
            <div className="rule" style={{ background: "#4FC3F7" }} />
            <div className="eyebrow !text-brand-light">Cloud backup, done for you</div>

            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-[56px] font-extrabold leading-[1.08] tracking-tight text-white">
              Managed cloud backup for Indian businesses.
            </h1>
            <p className="mt-5 text-lg sm:text-xl text-brand-mist leading-relaxed max-w-2xl">
              We install it, it backs up daily by itself, and we restore your files when you
              need them. No software to learn. No buttons to press.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <Link
                to="/get-started"
                className="inline-flex items-center gap-2 rounded-md text-white px-7 py-3.5 font-bold text-base shadow-card-soft hover:shadow-card-lift hover:-translate-y-px transition-all"
                style={{ background: "linear-gradient(100deg,#1D6C35 0%,#2C994D 100%)" }}
                data-testid="hero-cta-primary"
              >
                Get Protected
                <ArrowRight className="h-4 w-4" strokeWidth={2.4} />
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 rounded-md border border-brand-light/50 hover:border-brand-light text-white px-6 py-3 font-medium text-base transition-colors"
                data-testid="hero-cta-secondary"
              >
                See Pricing <ArrowRight className="h-4 w-4" strokeWidth={1.75} />
              </Link>
            </div>

            <p className="mt-8 text-sm text-brand-pale/80">
              ISO 27001-certified storage · Encrypted end-to-end · Same-day setup
            </p>
          </div>
        </div>
      </section>

      {/* WHY VAULTSAFE — 4 flat cards, white bg */}
      <section className="bg-white">
        <div className="mx-auto max-w-site px-5 lg:px-8 py-20 lg:py-24">
          <SectionRule eyebrow="Why VaultSafe" />
          <h2 className="text-3xl sm:text-4xl font-bold text-ink leading-tight max-w-2xl">
            Backup, done for you. Not another thing on your list.
          </h2>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {
                icon: ShieldCheck,
                title: "Fully managed",
                body:
                  "We set it up, we watch it, we fix it. You're not learning any software.",
              },
              {
                icon: RefreshCw,
                title: "Runs by itself",
                body:
                  "Daily automatic backups. No reminders. No \"I'll do it Sunday.\"",
              },
              {
                icon: Lock,
                title: "ISO 27001 storage",
                body:
                  "Encrypted, geographically redundant enterprise-grade data centres.",
              },
              {
                icon: Users,
                title: "Real people, local support",
                body:
                  "Based in Mumbai. Call or WhatsApp — you'll speak to someone who knows your setup.",
              },
            ].map((c, i) => (
              <div
                key={i}
                className="rounded-card bg-brand-card p-6"
                data-testid={`why-card-${i}`}
              >
                <div className="inline-flex items-center justify-center h-11 w-11 rounded-md bg-white text-brand">
                  <c.icon className="h-5 w-5" strokeWidth={1.75} />
                </div>
                <h3 className="mt-5 text-lg font-semibold text-ink">{c.title}</h3>
                <p className="mt-2 text-sm text-ink-body leading-relaxed">{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — 3 numbered steps, brand-mist band */}
      <section style={{ backgroundColor: "#F8FAFC" }}>
        <div className="mx-auto max-w-site px-5 lg:px-8 py-20 lg:py-24">
          <SectionRule eyebrow="How it works" />
          <h2 className="text-3xl sm:text-4xl font-bold text-ink leading-tight max-w-2xl">
            Getting protected is effortless.
          </h2>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                n: 1,
                icon: Download,
                title: "We install it.",
                body:
                  "A small, quiet program on each computer. 15 minutes, once. You don't touch it again.",
              },
              {
                n: 2,
                icon: RefreshCw,
                title: "It backs up daily, by itself.",
                body:
                  "Your important folders are copied to secure cloud storage. No buttons to press.",
              },
              {
                n: 3,
                icon: Undo2,
                title: "You get your files back.",
                body:
                  "Deleted something? Machine died? Ransomware? Tell us. We restore.",
              },
            ].map((s) => (
              <div key={s.n} className="text-left">
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 rounded-full bg-brand text-white flex items-center justify-center font-bold text-lg">
                    {s.n}
                  </div>
                  <s.icon className="h-5 w-5 text-brand" strokeWidth={1.75} />
                </div>
                <h3 className="mt-5 text-xl font-semibold text-ink">{s.title}</h3>
                <p className="mt-2 text-ink-body leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT GETS PROTECTED + THE RISK — 2 columns */}
      <section className="bg-white">
        <div className="mx-auto max-w-site px-5 lg:px-8 py-20 lg:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Left — green pills */}
          <div>
            <SectionRule eyebrow="What gets protected" />
            <h2 className="text-3xl font-bold text-ink leading-tight">
              Every file your business runs on.
            </h2>
            <p className="mt-4 text-ink-body leading-relaxed">
              We back up the folders you choose — Tally, documents, records, drawings.
              Not Windows, not programs. Just what matters.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {[
                "Tally data",
                "Client documents",
                "GST records",
                "Balance sheets",
                "Design & CAD files",
                "Case files",
                "Patient records",
                "Invoices & receipts",
                "Emails & PDFs",
                "Scanned records",
              ].map((p, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium"
                  style={{ background: "#E8F4EA", color: "#1D6C35" }}
                >
                  <Check className="h-3.5 w-3.5" strokeWidth={2.6} />
                  {p}
                </span>
              ))}
            </div>
          </div>

          {/* Right — risk list */}
          <div>
            <SectionRule eyebrow="The risk every business faces" />
            <h2 className="text-3xl font-bold text-ink leading-tight">
              Data disappears when you least expect it.
            </h2>
            <ul className="mt-6 space-y-3">
              {[
                "Ransomware locks every file on your PC.",
                "The hard drive dies without any warning.",
                "An employee deletes the wrong folder before leaving.",
                "Theft, fire, flood — the machine is gone.",
              ].map((r, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span
                    className="mt-0.5 h-5 w-5 rounded-full flex items-center justify-center shrink-0"
                    style={{ background: "#FEE2E2", color: "#DC2626" }}
                  >
                    <X className="h-3 w-3" strokeWidth={3} />
                  </span>
                  <span className="text-ink-body">{r}</span>
                </li>
              ))}
            </ul>
            <div
              className="mt-6 rounded-card p-5 flex items-start gap-3"
              style={{ background: "#E8F4EA", borderLeft: "4px solid #2C994D" }}
            >
              <Cloud className="h-5 w-5 text-leaf-dark mt-0.5 shrink-0" strokeWidth={1.75} />
              <div>
                <p className="font-semibold text-leaf-dark">
                  Every one of these is what backup exists for.
                </p>
                <p className="mt-1 text-sm text-ink-body leading-relaxed">
                  Your data isn't only on your machine — a fresh copy sits somewhere else.
                  Whichever thing above happens, we restore, and you're back to work.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section style={{ backgroundColor: "#F8FAFC" }}>
        <div className="mx-auto max-w-site px-5 lg:px-8 py-20 lg:py-24">
          <SectionRule eyebrow="Who it's for" />
          <h2 className="text-3xl sm:text-4xl font-bold text-ink leading-tight max-w-2xl">
            Built for the businesses that keep India moving.
          </h2>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {[
              { t: "CA & Tax Firms", b: "Returns, balance sheets, Tally data." },
              { t: "Doctors & Clinics", b: "Patient records and billing data." },
              { t: "Architects & Designers", b: "Drawings and project files." },
              { t: "Law Firms", b: "Case files, contracts, filings." },
              { t: "Retail & Wholesale", b: "Tally, invoices, GST, stock." },
              { t: "Manufacturing & MSMEs", b: "BOMs, quotations, dispatch." },
              { t: "Coaching & Schools", b: "Study material, student records." },
              { t: "Real Estate", b: "Property files, agreements." },
              { t: "Media & Agencies", b: "Client work, campaign assets." },
              { t: "Freelancers", b: "Years of your work." },
            ].map((w, i) => (
              <div key={i} className="rounded-card bg-white p-4">
                <h3 className="text-sm font-semibold text-ink leading-snug">{w.t}</h3>
                <p className="mt-1.5 text-xs text-ink-body leading-relaxed">{w.b}</p>
              </div>
            ))}
          </div>

          <p className="mt-8 text-sm text-ink-body/80">
            Not on the list? If it runs on a Windows PC, we can protect it.{" "}
            <Link
              to="/contact"
              className="text-brand underline hover:no-underline font-medium"
            >
              Ask us
            </Link>
            .
          </p>
        </div>
      </section>

      {/* DARK STAT BAND */}
      <section className="bg-navy text-white">
        <div className="mx-auto max-w-site px-5 lg:px-8 py-14 lg:py-16">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { k: "15 min", v: "Setup per PC" },
              { k: "Daily", v: "Automatic backups" },
              { k: "ISO 27001", v: "Certified storage" },
              { k: "Same-day", v: "Restore turnaround" },
            ].map((s, i) => (
              <div key={i} className="text-center lg:text-left">
                <div className="text-3xl lg:text-4xl font-extrabold text-brand-light tracking-tight">
                  {s.k}
                </div>
                <div className="mt-1 text-sm text-brand-pale">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING TEASER */}
      <section className="bg-white">
        <div className="mx-auto max-w-site px-5 lg:px-8 py-20 lg:py-24">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
            <div>
              <SectionRule eyebrow="Pricing" />
              <h2 className="text-3xl sm:text-4xl font-bold text-ink leading-tight">
                Simple, honest pricing.
              </h2>
              <p className="mt-2 text-ink-body">Per computer, per month. Billed annually.</p>
            </div>
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 rounded-md border border-brand text-brand hover:bg-brand hover:text-white px-5 py-2.5 text-sm font-semibold transition-colors self-start"
            >
              See Full Pricing <ArrowRight className="h-4 w-4" strokeWidth={1.75} />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {plans.map((p) => (
              <div
                key={p.id}
                className={`relative rounded-card p-5 transition-all ${
                  p.highlight
                    ? "bg-white border-2 border-brand shadow-card-lift lg:-translate-y-2"
                    : "bg-brand-card"
                }`}
                data-testid={`pricing-teaser-${p.id}`}
              >
                {p.badge && (
                  <span className="absolute -top-3 left-4 rounded-full bg-brand text-white text-[10px] font-bold px-2.5 py-1 uppercase tracking-wider">
                    {p.badge}
                  </span>
                )}
                <h3 className="text-lg font-bold text-ink">{p.name}</h3>
                <p className="mt-1 text-xs text-ink-body">{p.tagline}</p>
                <div className="mt-4">
                  <span className="text-2xl font-extrabold text-ink">
                    ₹{p.price.toLocaleString("en-IN")}
                  </span>
                  <span className="text-xs text-ink-body"> /PC/mo</span>
                </div>
                <ul className="mt-4 space-y-1.5 text-sm text-ink-body">
                  <li>{p.storage} storage</li>
                  <li>{p.history} file history</li>
                  <li>{p.support} support</li>
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GREEN CTA BAND — closes the page */}
      <section style={{ background: "linear-gradient(100deg,#1D6C35 0%,#2C994D 100%)" }}>
        <div className="mx-auto max-w-site px-5 lg:px-8 py-16 lg:py-20">
          <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-8 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-white leading-tight max-w-2xl">
                Most people back up their data after they lose it once. Don't be most people.
              </h2>
              <p className="mt-3 text-white/85 max-w-xl">
                Two-minute form. We'll get back to you within one business day.
              </p>
            </div>
            <div className="flex md:justify-end">
              <Link
                to="/get-started"
                className="inline-flex items-center gap-2 rounded-md bg-white text-leaf-dark px-6 py-3.5 font-bold text-base shadow-card-lift hover:-translate-y-px transition-all"
                data-testid="closer-cta"
              >
                Get Protected
                <ArrowRight className="h-4 w-4" strokeWidth={2.4} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
